import _ from 'lodash'
import {
  PRODUCT_ATTR_DISPLAY_MODE,
  PROPOSAL_TYPE
} from '@vzmi/shp_shared_compo/constants/proposal'
import fetchCategoryOptions from '../request/fetchCategoryOptions'
import fetchStruDataAttrClusterOptions from '../request/fetchStruDataAttrClusterOptions'
import { ATTRIBUTE_CONSTRAINTS_TYPE, OTHER_ATTRIBUTE_VALUE } from '../../../constants/proposalTypes'
import { SELECTED_VALUES_SELECTED, ATTRIBUTE_DISPLAY_MODE_SELECTED } from '../../../constants/batchProposals'
import { UPDATE_LISTING_ATTRIBUTE } from '../../Proposal/UpdateListing/helpers/converters'
import { API_ERROR_MAPPING } from '../../../constants/globalTypes'
import {
  convertClusterToAttributeOption
} from '../../Proposal/Common/ProposalForm/Shared/util'

export const getConvertedAttributeListingParams = listing => {
  const convertedListing = _.flow(
    // 抽出 update attribute 會用到的欄位
    () => _.pick(listing, [
      'id',
      'attributeDisplayMode',
      'attributes',
      'struDataAttrClusterId'
    ]),
    attrListing => {
      const {
        attributes = [],
        ...restListing
      } = attrListing
      const convertedAttributes = _.flow(
        // 過濾沒有填寫數值的規格表項目
        attrs => _.filter(attrs, attr => !_.isEmpty(attr.selectedValues)),
        // 重組有值的規格表項目結構成 API 指定格式
        nonEmptyAttrs => _.map(nonEmptyAttrs, attr => {
          const constraintsType = _.get(attr, 'constraints.type')
          let convertedAttribute = attr
          switch (constraintsType) {
            // 一般輸入框會取得字串格式，API 每個規格表項目吃的是陣列格式
            // 所以 'test' 會轉成 ['test']
            case ATTRIBUTE_CONSTRAINTS_TYPE.TEXT:
            case ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM:
              convertedAttribute = {
                name: attr.name,
                selectedValues: [_.get(attr, 'selectedValues', '')]
              }
              break
            // 其他選單不論單選或多選，預設格式都是陣列 ['a', 'b', 'c']
            case ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX:
            case ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX:
            default:
              break
          }
          // update attribute 接受的 attributes 格式
          const paramAttribute = {
            name: convertedAttribute.name,
            values: convertedAttribute.selectedValues
          }
          return paramAttribute
        })
      )(attributes)
      return { ...restListing, attributes: convertedAttributes }
    }
  )()
  return convertedListing
}

export const getParamsList = (formValues, reviewStatus, duplicatedMap) => {
  const {
    applicant,
    listings = []
  } = formValues
  // 共用商品賣場不能修改所以預先剔除
  const filteredListings = _.filter(listings, listing => !(listing.id in duplicatedMap))
  const updateParamsList = _.map(filteredListings, listing => {
    const {
      proposal: {
        id: proposalId
      } = {}
    } = listing
    const convertedListing = getConvertedAttributeListingParams(listing)
    const updateParams = {
      reviewStatus,
      applicant,
      id: proposalId,
      // update attribute 吃的 type 非預設 proposalType
      type: UPDATE_LISTING_ATTRIBUTE,
      listing: {
        ...convertedListing
      }
    }
    return updateParams
  })
  return updateParamsList
}

export const getListingsFieldErrors = (listings, failedProposalMap) => {
  const listingsFieldErrors = {}
  // failedProposalMap 是空的代表完成、送出提案全部成功，所以沒有錯誤欄位
  if (_.isEmpty(failedProposalMap)) {
    return listingsFieldErrors
  }

  // 含有錯誤欄位時以目前表單跑回圈依序確認是哪些賣場更新規格表失敗
  // 寫入指定賣場的指定欄位需要從表單的賣場順序判斷
  // 因為更新規格表是單筆送出，所以 api error 裡不會有賣場順序
  for (const [index, listing] of listings.entries()) {
    const convertedAttrs = _.get(getConvertedAttributeListingParams(listing), 'attributes', [])
    const { id, attributes: formAttrs = [] } = listing
    const { error } = _.get(failedProposalMap, id, {})
    const details = _.get(error, 'detail', [])
    // 錯誤訊息如果來自修改賣場資訊會沒有 detail，直接把 invalidValue, message 會直接在 error 底下
    const errors = _.isEmpty(details) ? [error] : details
    for (const error of errors) {
      if (_.isUndefined(error)) {
        return
      }

      const { invalidValue = '', message } = error
      // 找出該賣場中 API 驗證失敗的欄位順序
      // proposal.listing.attributes[17].values > 17
      const extraAttrIndexFromError = _.get(invalidValue.match(/attributes\[(\d+)\]/), '1')
      // 送出的商品規格會過濾掉值為空的規格，所以 API 回傳的位置也會是過濾過的順序
      // 所以要先從 API 取得的 index 找到送出時過濾後對應的規格欄位名稱
      // 再回去找表單中商品規格對應的欄位名稱才是真正的錯誤欄位
      const formAttrIndexPath = _.flow(
        () => _.get(convertedAttrs, `[${extraAttrIndexFromError}].name`),
        invalidAttrName => _.findIndex(formAttrs, { name: invalidAttrName }),
        // 萬一找不到代表有問題不使用
        invalidAttrIndex => (invalidAttrIndex !== -1) ? `attributes[${invalidAttrIndex}]` : ''
      )()
      // 找出驗證失敗欄位對應的 API error code
      // [40009112] Product/Listing attribute > 40009112
      const extraErrorCodeFromError = _.get(message.match(/^\[(\d+)/), '1')
      const currentCodeErrorMessage = _.get(API_ERROR_MAPPING, extraErrorCodeFromError, message)
      // 組成 redux form 的錯誤訊息
      // listings[0].attributes[17].selectedValues
      const fieldPathFromReduxForm = `listings[${index}].${formAttrIndexPath}.selectedValues`
      if (!_.isEmpty(formAttrIndexPath) && !_.isEmpty(currentCodeErrorMessage)) {
        _.set(listingsFieldErrors, fieldPathFromReduxForm, currentCodeErrorMessage)
      }
    }
  }
  // errors format
  // {
  //   listings: [{
  //     attributes: [{
  //       selectedValues: 'error message'
  //     }]
  //   }]
  // }
  return listingsFieldErrors
}

export const getLayerModelOptions = models => {
  const [
    layerOneModel,
    layerTwoModel
  ] = _.flow(
    () => _.reduce(models, (collect, model) => {
      const spec = _.get(model, 'spec', {})
      const itemSpecs = _.map(_.get(model, 'items', []), 'spec')
      // 分別將第一層、第二層各自獨立合併
      collect[0].push(spec)
      collect[1] = [...collect[1], ...itemSpecs]
      return collect
    }, [[], []]),
    specsByLayer => _.map(specsByLayer, specs => {
      const uniqSpecs = _.flow(
        () => _.filter(specs, (spec = {}) => !_.isEmpty(spec.selectedValue)),
        nonEmptySpecs => _.uniqBy(nonEmptySpecs, 'selectedValue')
      )()
      const modelSpecName = _.get(uniqSpecs, '0.name')
      // 屬性名稱顯示方式是 select 所以組成一個選項
      const specOptions = [{ label: modelSpecName, value: modelSpecName }]
      const specOptionValues = [modelSpecName]
      // 選擇的屬性為多筆，找出不重複的值後組成選項
      const selectedValueOptions = _.map(uniqSpecs, spec => {
        const selectedValue = _.get(spec, 'selectedValue')
        return { label: selectedValue, value: selectedValue }
      })
      const selectedValueOptionValues = _.map(selectedValueOptions, 'value')
      const model = {
        ...(
          !_.isEmpty(specOptionValues[0]) &&
          { specOptions, specOptionValues }
        ),
        ...(
          !_.isEmpty(selectedValueOptionValues[0]) &&
          { selectedValueOptions, selectedValueOptionValues }
        )
      }
      return model
    })
  )()
  // 無屬性 [{}, {}]
  // 單層屬性 [{ specOptions, ... }, {}]
  // 雙層屬性 [{ specOptions, ... }, { specOptions, ... }]
  return [layerOneModel, layerTwoModel]
}

const STRING_ATTRIBUTE_CONSTRAINTS_TYPES = [
  ATTRIBUTE_CONSTRAINTS_TYPE.TEXT,
  ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM
]

export const getConvertedAttributeWithDefaultValue = defaultAttributeMap => attribute => {
  const { name, constraints, hasOTHER, values = [] } = attribute
  const attrType = _.get(constraints, 'type')
  // 預設允許的選項值
  const optionValuesMap = _.keyBy(values)
  // 判斷選項值是否有`其他`，有這個值代表可以自訂
  const isOtherOptionExist = (
    // 資料轉換過後其他會被剔除，並且 hasOTHER === true
    hasOTHER ||
    // 沒轉換過之前可能會有 `其他`
    (OTHER_ATTRIBUTE_VALUE in optionValuesMap)
  )
  // 賣場預設已有寫入資料的規格項目
  // get key 加上 [] 避免遇到商品規格名稱含有 `.` 會被判斷成下一層 ex: `2.5吋傳統硬碟容量`
  let convertedValues = _.get(defaultAttributeMap, `["${name}"].values`, [])
  // 按照規格名稱對應欄位應有的格式，如果格式應為 text 但資料卻是 array 就用逗號組合起來
  // EX: ['1', '2', '3'] > '1,2,3'
  // 這個是兼容舊系統修改過後，規格表的值會被轉換成陣列導致送給 api 時會得到屬性或規格過多的錯誤訊息
  if (STRING_ATTRIBUTE_CONSTRAINTS_TYPES.includes(attrType)) {
    if (_.isArray(convertedValues) && (_.size(convertedValues) > 1)) {
      convertedValues = _.join(convertedValues, ',')
    } else {
      convertedValues = _.get(convertedValues, '0', '')
    }
  } else if (!_.isEmpty(convertedValues) && !isOtherOptionExist) {
    // 規格為選項且不可自訂時驗證預設選取值是否在預設允許的選項裡
    const isInvalidValue = _.some(convertedValues, convertedValue => !(convertedValue in optionValuesMap))
    // 不在預設允許的選項裡則清空規格的預設值
    if (isInvalidValue) {
      convertedValues = []
    }
  }
  const convertedAttribute = {
    ...attribute,
    values: convertedValues,
    selectedValues: convertedValues
  }
  return convertedAttribute
}

export const getListingAttributesFromFormAttrs = formAttrs => {
  const listingAttributes = _.map(formAttrs, attr => {
    const {
      name,
      values,
      selectedValues,
      constraints
    } = attr
    const currentValues = (
      // 確認頁、okta redirect listing.attributes
      selectedValues ||
      // 檢視頁 proposal.listing.attributes
      values
    )
    const type = _.get(constraints, 'type')
    // 表單輸入框的值是取陣列第一項的值存成字串格式
    // 要把表單資料作成 API 取得的賣場規格項目預設值資料格式，所以需要轉回陣列
    if (STRING_ATTRIBUTE_CONSTRAINTS_TYPES.includes(type)) {
      return { name, values: [currentValues] }
    }
    return { name, values: currentValues }
  })
  return listingAttributes
}

export const getConvertedAttributeListing = listing => {
  const {
    attributeDisplayMode = PRODUCT_ATTR_DISPLAY_MODE.TABLE,
    productStructuredDataList,
    attributes: formAttributes,
    struDataAttrClusterId: struDataAttrClusterIdFromState,
    isInConsistentProduct: isInConsistentProductFromState
  } = listing
  const {
    // 規格表 id
    struDataAttrClusterId: struDataAttrClusterIdFromDataList,
    // 預設已選擇的規格表內容
    attributes: defaultAttributes = []
  } = _.get(productStructuredDataList, 0, {})
  // 進入 "確認頁" 或是 "okta 逾時 redirect" 取得的 listing 會是 convertedListing
  // 所以可以直接取得目前規格項目(listing.attributes)
  // 否則預設已填值的規格項目從賣場的規格表內取得
  const currentAttributes = formAttributes || defaultAttributes
  // 目前選擇的規格表內容轉成對應表，待取得完整的規格表項目後可以帶入預設值
  const convertedAttributeMap = _.flow(
    attrs => {
      if (formAttributes) {
        return getListingAttributesFromFormAttrs(attrs)
      }
      return attrs
    },
    // 組成對應表前加上 index 確保對應出來的值如果需要排序時可以參考
    // ex: 規格表內容含有自訂規格在檢視頁這些自訂規格需要有 index 才能知道先後順序
    attrs => _.map(attrs, (attr, index) => ({ ...attr, index })),
    attrsWithIndex => _.keyBy(attrsWithIndex, 'name')
  )(currentAttributes)
  const models = _.get(listing, 'models', [])
  const hasSpecLevel = !_.isEmpty(models)
  // 組出 redux form 方便使用的資料格式
  const [layerOneModel, layerTwoModel] = getLayerModelOptions(models)
  // 判斷賣場裡商品是否規格表編號一致
  // 如果 isInConsistentProductFromState 有值代表賣場已是轉換過的資料
  // 則沿用來直接判斷賣場商品是否規格表編號不一致 (確認頁用到)
  const isInConsistentProduct = _.isUndefined(isInConsistentProductFromState)
    ? _.flow(
      () => _.map(productStructuredDataList, 'struDataAttrClusterId'),
      _.uniq,
      struDataAttrClusterIds => _.size(struDataAttrClusterIds) > 1
    )()
    : isInConsistentProductFromState
  const struDataAttrClusterId = (
    struDataAttrClusterIdFromState ||
    struDataAttrClusterIdFromDataList
  )

  const convertedAttributeListing = {
    ...listing,
    attributeDisplayMode,
    // 規格表編號是否一致，不一致時需要重選規格表
    // 選擇後規格表選單會將該賣場 isInConsistentProduct 改成 false
    isInConsistentProduct,
    struDataAttrClusterId,
    // 按照[規格表編號]儲存選擇的規格表內容
    // 避免重新選擇規格表後不同規格表項目遇到相同欄位名稱
    convertedAttributeMapByClusterId: {
      [struDataAttrClusterId]: convertedAttributeMap
    },
    attributes: currentAttributes,
    hasSpecLevel,
    // 規格表選單預設不可選取，但是選單在預設選項沒有選滿 3 項時會開放選取
    // 選擇後規格表選單會將該賣場 isNewPathSelectable 改成 true
    // 因為只要進入賣場當下規格表有問題就允許重選規格表
    isNewPathSelectable: false,
    // 無屬性賣場不會有底下的欄位 layerOneModel, layerTwoModel
    ...(!_.isEmpty(layerOneModel) && { layerOneModel }),
    ...(!_.isEmpty(layerTwoModel) && { layerTwoModel })
  }
  return convertedAttributeListing
}

export const getConvertedListings = listings => {
  const convertedListings = _.map(listings, getConvertedAttributeListing)
  return convertedListings
}

export const getUpdatedFormListingWithAttributes = (convertedListings, menuData) => {
  const updatedFormListingWithAttributes = _.map(convertedListings, convertedListing => {
    const {
      struDataAttrClusterId,
      convertedAttributeMapByClusterId,
      layerOneModel,
      layerTwoModel
    } = convertedListing

    // 目前賣場的單雙層屬性
    const modelSpecs = _.filter([
      _.get(layerOneModel, 'specOptionValues[0]'),
      _.get(layerTwoModel, 'specOptionValues[0]')
    ], spec => !_.isUndefined(spec))
    // 根據規格表 id 取得對應賣場已寫入值的規格項目名稱對應表
    const convertedAttributeMap = _.get(convertedAttributeMapByClusterId, struDataAttrClusterId, {})
    const formattedAttributesWithoutModelSpecs = _.flow(
      // 根據規格表 id 取得該規格設的規格項目
      () => _.get(menuData, `${CATEGORY_LAYER.FOUR}.${struDataAttrClusterId}[0].convertedAttributes`, []),
      attributes => {
        // 已在商品屬性顯示的屬性從規格表項目中過濾掉
        return _.filter(attributes, attribute => !_.includes(modelSpecs, attribute.name))
      },
      uniqAttributes => _.map(uniqAttributes, uniqAttribute => {
        const attrType = _.get(uniqAttribute, 'constraints.type')
        // 文字類型的規格項目預設值配合 redux form 資料寫入格式改成空字串
        if (attrType === ATTRIBUTE_CONSTRAINTS_TYPE.TEXT) {
          return { ...uniqAttribute, values: '' }
        }
        return uniqAttribute
      })
    )()

    // 沒有已寫入值的規格項目則該賣場不需要逐項塞入賣場的預設值
    if (_.isEmpty(convertedAttributeMap)) {
      return { ...convertedListing, attributes: formattedAttributesWithoutModelSpecs }
    }

    const attributesWithDefaultSelectedValues = _.map(
      formattedAttributesWithoutModelSpecs,
      // 增加 selectedValues 欄位給 redux form 綁定變更時時要修改的欄位
      getConvertedAttributeWithDefaultValue(convertedAttributeMap)
    )
    // 不在規格表內的屬性項目為自訂規格，加在規格項目最後面(只有檢視頁會遇到)
    const convertedAttributesWithCustomAttrs = getAttributesWithCustomAttrs(
      attributesWithDefaultSelectedValues,
      convertedAttributeMap
    )
    return { ...convertedListing, attributes: convertedAttributesWithCustomAttrs }
  })
  return updatedFormListingWithAttributes
}

export const getAttributesWithCustomAttrs = (attributes, attributeMap) => {
  // 扣掉規格表取得的規格項目，多出來的代表是自訂規格
  const customAttributeMap = _.omit(attributeMap, _.map(attributes, 'name'))
  if (_.isEmpty(customAttributeMap)) {
    return attributes
  }

  // 轉換預設值成相容 redux form field 的格式
  const getConvertedAttributeByMap = getConvertedAttributeWithDefaultValue(customAttributeMap)
  const customAttributes = _.flow(
    // 取得自訂規格的項目集合
    () => _.values(customAttributeMap),
    attributes => _.map(attributes, attribute => {
      // 組出帶預設值的自訂規格欄位
      const attributeField = getConvertedAttributeByMap({
        ...DEFAULT_CUSTOM_ATTRIBUTE_FIELD,
        ...attribute
      })
      return attributeField
    }),
    // 重新排序自訂規格項目確保跟 API 取回順序一致
    // 按照 index 由小到大排序
    attributeFields => _.orderBy(attributeFields, ['index'], 'asc')
  )()

  return [...attributes, ...customAttributes]
}

export const getUniqAndNonEmptyParams = (paramsByLayer, layerKey) => {
  // 同層請求都有一個用來查詢的編號，用來確保同一層相同編號只會發出一次請求
  const uniqKey = _.get(MENU_PARAM_UNIQ_KEY, layerKey)
  // 找出按層分群的請求內容
  const params = _.get(paramsByLayer, layerKey, [])
  const uniqAndNonEmptyParams = _.flow(
    params => _.uniqBy(params, uniqKey),
    uniqParams => _.filter(uniqParams, param => !_.isEmpty(param))
  )(params)
  return uniqAndNonEmptyParams
}

export const getCategoryOptionsMap = (listings, menuData, originCategoryOptionsMap) => {
  // 從既有對應表組出新的對應表
  // 指定賣場已選擇[子站、父類、子類、規格]項目
  const selectedCategoryOptionByListingId = _.cloneDeep(originCategoryOptionsMap.listing)
  // 指定子站的選單[父類、子類、規格]項目
  const categoryOptionsBySubStationId = _.cloneDeep(originCategoryOptionsMap.subStation)
  for (const currentListing of listings) {
    const { id, subStationId } = currentListing
    // 根據賣場的資料找出已選擇的項目
    const selectedCategoryOption = getSelectedCategoryOption(currentListing, menuData)
    const currentCategoryOptions = _.get(categoryOptionsBySubStationId, subStationId, [])
    let newCategoryOptions = []
    // 第一次產生對應表時會逐層(layer)組出該子站展開選單時要列出的選項
    // 重新選擇不同父類時也會重組一整套新的展開選單時要列出的選項
    if (_.isEmpty(currentCategoryOptions)) {
      newCategoryOptions = getCategoryOptions(currentListing, menuData)
    } else {
      // 在既有的父類下選擇不同的子類、規格則會將新的選項附加到既有的選項裡
      newCategoryOptions = getCombindCategoryOptions(currentListing, currentCategoryOptions, menuData)
    }
    _.set(selectedCategoryOptionByListingId, id, selectedCategoryOption)
    _.set(categoryOptionsBySubStationId, subStationId, newCategoryOptions)
  }
  return {
    listing: selectedCategoryOptionByListingId,
    subStation: categoryOptionsBySubStationId
  }
}

export const CATEGORY_LAYER = {
  // 子站底下的父類選項
  ONE: 'layerOne',
  // 父類底下的子類選項
  TWO: 'layerTwo',
  // 子類底下的規格選項
  THREE: 'layerThree',
  // 規格底下的規格項目(實際主要修改的規格項目列表)
  FOUR: 'layerFour'
}

const DEFAULT_MENU_PARAM_UNIQ_KEY = 'categoryId'

const LAYER_KEY = 'layer'

export const DEFAULT_MENU_DATA = {
  [CATEGORY_LAYER.ONE]: {},
  [CATEGORY_LAYER.TWO]: {},
  [CATEGORY_LAYER.THREE]: {},
  [CATEGORY_LAYER.FOUR]: {}
}

const MENU_PARAM_UNIQ_KEY = {
  [CATEGORY_LAYER.ONE]: DEFAULT_MENU_PARAM_UNIQ_KEY,
  [CATEGORY_LAYER.TWO]: DEFAULT_MENU_PARAM_UNIQ_KEY,
  [CATEGORY_LAYER.THREE]: DEFAULT_MENU_PARAM_UNIQ_KEY,
  [CATEGORY_LAYER.FOUR]: 'id'
}

const DEFAULT_ATTR_MENU_PARAMS = {
  [CATEGORY_LAYER.ONE]: [],
  [CATEGORY_LAYER.TWO]: [],
  [CATEGORY_LAYER.THREE]: [],
  [CATEGORY_LAYER.FOUR]: []
}

export const DEFAULT_CUSTOM_ATTRIBUTE_FIELD = {
  constraints: {
    type: ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM
  },
  selectedValues: [],
  values: ''
}

export const getIsEmptyOrFailedResult = result => {
  // 沒有查過
  const isEmptyResult = _.isEmpty(result)
  // 查出來有錯誤訊息 (4xx, 5xx)
  const isFailedResult = !_.isUndefined(_.get(result, 'message'))
  return (isEmptyResult || isFailedResult)
}

export const getNonExistAttrMenuParams = (listings, menuData) => {
  const paramsByLayer = _.reduce(listings, (collects, listing) => {
    const {
      subStationId,
      catId,
      catItemId,
      struDataAttrClusterId
    } = listing
    const proposalType = PROPOSAL_TYPE.UPDATE_ATTRIBUTE
    // 父類 menu options
    const isLayerOneSearchable = (
      !_.isUndefined(subStationId) &&
      getIsEmptyOrFailedResult(_.get(menuData, `${CATEGORY_LAYER.ONE}.${subStationId}`))
    )
    const layerOneParam = {
      ...(
        isLayerOneSearchable &&
        { categoryId: subStationId, fields: ['children', 'parents'], proposalType }
      )
    }
    // 子纇 menu options
    const isLayerTwoSearchable = (
      !_.isUndefined(catId) &&
      getIsEmptyOrFailedResult(_.get(menuData, `${CATEGORY_LAYER.TWO}.${catId}`))
    )
    const layerTwoParam = {
      ...(
        isLayerTwoSearchable &&
        { categoryId: catId, fields: ['children'], proposalType }
      )
    }
    // 規格 menu options
    const isLayerThreeSearchable = (
      !_.isUndefined(catItemId) &&
      getIsEmptyOrFailedResult(_.get(menuData, `${CATEGORY_LAYER.THREE}.${catItemId}`))
    )
    const layerThreeParam = {
      ...(
        isLayerThreeSearchable &&
        { categoryId: catItemId, proposalType }
      )
    }
    // 規格表項目
    const isLayerFourSearchable = (
      !_.isUndefined(struDataAttrClusterId) &&
      getIsEmptyOrFailedResult(
        _.get(menuData, `${CATEGORY_LAYER.FOUR}.${struDataAttrClusterId}[0].convertedAttributes`)
      )
    )
    const layerFourParam = {
      ...(
        isLayerFourSearchable &&
        { id: struDataAttrClusterId }
      )
    }
    collects[CATEGORY_LAYER.ONE].push(layerOneParam)
    collects[CATEGORY_LAYER.TWO].push(layerTwoParam)
    collects[CATEGORY_LAYER.THREE].push(layerThreeParam)
    collects[CATEGORY_LAYER.FOUR].push(layerFourParam)
    return collects
  }, _.cloneDeep(DEFAULT_ATTR_MENU_PARAMS))
  const uniqLayerOneParams = getUniqAndNonEmptyParams(paramsByLayer, CATEGORY_LAYER.ONE)
  const uniqLayerTwoParams = getUniqAndNonEmptyParams(paramsByLayer, CATEGORY_LAYER.TWO)
  const uniqLayerThreeParams = getUniqAndNonEmptyParams(paramsByLayer, CATEGORY_LAYER.THREE)
  const uniqLayerFourParams = getUniqAndNonEmptyParams(paramsByLayer, CATEGORY_LAYER.FOUR)
  // 全部賣場每一層的資料在打 API 前都會從這裡判斷，
  // 已經存在就不再產生新的 params 給 API 打
  // 避免大量重複請求造成伺服器嚴重負擔
  return {
    [CATEGORY_LAYER.ONE]: uniqLayerOneParams,
    [CATEGORY_LAYER.TWO]: uniqLayerTwoParams,
    [CATEGORY_LAYER.THREE]: uniqLayerThreeParams,
    [CATEGORY_LAYER.FOUR]: uniqLayerFourParams
  }
}

export const getConvertedAttributeWithOptions = responseResult => {
  const [error, struDataAttrClusterOptions = []] = responseResult
  // 將資料轉換成適合 redux form 的格式
  const convertedData = _.map(struDataAttrClusterOptions, struDataAttrClusterOption => {
    const convertedAttributes = _.flow(
      () => _.get(struDataAttrClusterOption, 'attrCluster.attributes', []),
      attributes => _.map(attributes, attribute => {
        // 組成新的Attribute，裡面判斷values是否有「其他」，把它濾掉並添加hasOTHER屬性
        const newAttribute = convertClusterToAttributeOption(attribute)
        // 組成與原本attribute 一樣格式
        const convertOldAttribute = _.get(newAttribute, 'cluster', {})
        const type = _.get(convertOldAttribute, 'constraints.type')
        const convertedAttribute = { ...convertOldAttribute, selectedValues: [] }
        // 欄位類型為字串時不進行轉換
        if (STRING_ATTRIBUTE_CONSTRAINTS_TYPES.includes(type)) {
          return convertedAttribute
        }

        // 其他類型會增加把 values 轉換成選單的選項 options 欄位
        const options = _.flow(
          () => _.get(convertOldAttribute, 'values', []),
          values => _.map(values, value => ({ label: value, value }))
        )()
        return { ...convertedAttribute, options }
      })
    )()
    return { ...struDataAttrClusterOption, convertedAttributes }
  })
  return [error, convertedData]
}

export const getMenuResults = (menuParams, actions) => {
  // 全部賣場資料的請求合併發出，但是每一層可以獨立處理自己需要的資料
  // 或是待回應後需要判斷資料屬於哪一層時會多塞該回應屬於哪一層(layer)
  const menuResults = Promise.all([
    ..._.map(
      _.get(menuParams, CATEGORY_LAYER.ONE, []),
      param => fetchCategoryOptions(actions, param)
        // 在回應裡插入 layer 之才才能分辨這個回應源自哪一層的請求
        .then(getResultWithArgs(param, CATEGORY_LAYER.ONE))
    ),
    ..._.map(
      _.get(menuParams, CATEGORY_LAYER.TWO, []),
      param => fetchCategoryOptions(actions, param)
        .then(getResultWithArgs(param, CATEGORY_LAYER.TWO))
    ),
    ..._.map(
      _.get(menuParams, CATEGORY_LAYER.THREE, []),
      param => fetchStruDataAttrClusterOptions(actions, param)
        .then(getResultWithArgs(param, CATEGORY_LAYER.THREE))
    ),
    ..._.map(
      _.get(menuParams, CATEGORY_LAYER.FOUR, []),
      param => fetchStruDataAttrClusterOptions(actions, param)
        // 規格項目會將資料轉換成適合 redux form 的格式
        .then(getConvertedAttributeWithOptions)
        .then(getResultWithArgs(param, CATEGORY_LAYER.FOUR))
    )
  ])
  return menuResults
}

export const getResultWithArgs = (param, layer) => result => {
  const [error, data] = result
  // 資料回應時增加層(layer)欄位，方便資料歸屬
  if (!_.isEmpty(error)) {
    return { [LAYER_KEY]: layer, param, error }
  }
  return { [LAYER_KEY]: layer, param, data }
}

export const getCombinedMenuData = (originMenuData, convertedResult) => {
  const combinedMenuData = _.flow(
    // 回應資料根據層做分群
    () => _.groupBy(convertedResult, LAYER_KEY),
    layerData => _.reduce(layerData, (currentMenuData, data, layer) => {
      // 每一層根據該層的 id(MENU_PARAM_UNIQ_KEY) 將查詢結果組成對應表
      currentMenuData = {
        ...currentMenuData,
        [layer]: {
          ...currentMenuData[layer],
          ..._.reduce(data, (mapById, item) => {
            const key = _.get(item, `param.${MENU_PARAM_UNIQ_KEY[layer]}`)
            const { error, data } = item
            // 有錯誤塞錯誤進去，下次選擇該選項時 getIsEmptyOrFailedResult 會抓出這個錯誤
            // 讓使用者可以重新發出請求嘗試載入該項目的子選單
            mapById[key] = _.isEmpty(data) ? error : data
            return mapById
          }, {})
        }
      }
      return currentMenuData
      // 重新複製一份避免修改到原始資料
      // 預設資料如果已經存在會跟新的合併在一起
    }, _.cloneDeep(originMenuData))
  )()
  return combinedMenuData
}

export const getSelectedCategoryOption = (listing, menuData) => {
  const {
    catId,
    catItemId,
    struDataAttrClusterId
  } = listing
  // 從賣場資訊裡取得規格表選單對應選擇的選項
  const currentLayerOneOption = _.get(menuData, `${CATEGORY_LAYER.TWO}.${catId}[0]`, {})
  const currentLayerTwoOption = _.find(
    _.get(currentLayerOneOption, 'children', []),
    { value: catItemId }
  ) || {}
  // 從子類底下找出該賣場商品的規格表
  const currentLayerThreeOption = _.find(
    _.get(menuData, `${CATEGORY_LAYER.THREE}.${catItemId}`, []),
    { value: struDataAttrClusterId }
  )
  // 判斷規格表是不是從目前賣場的子類底下找到的
  const isClusterDependOnCatItem = !_.isEmpty(currentLayerThreeOption)
  // 從賣場商品的規格表編號取得規格表，這裡也拿不到代表整個規格表已不存在
  const currentStruDataAttrCluster = _.get(menuData, `${CATEGORY_LAYER.FOUR}.${struDataAttrClusterId}[0]`, {})

  const currentSelectedCategoryOption = {
    [CATEGORY_LAYER.ONE]: currentLayerOneOption,
    [CATEGORY_LAYER.TWO]: currentLayerTwoOption,
    [CATEGORY_LAYER.THREE]: isClusterDependOnCatItem
      ? currentLayerThreeOption
      : currentStruDataAttrCluster
  }
  return currentSelectedCategoryOption
}

export const getVisibleOptionsWithIndex = options => {
  const convertedOptions = _.flow(
    // 隱藏設定為不顯示的選項
    () => _.filter(options, option => {
      const { visible, isHide } = option
      const isVisible = _.isUndefined(visible) ? true : visible
      return isVisible && !isHide
    }),
    // 根據"文字"由小到大排序
    visibleOptions => _.orderBy(visibleOptions, ['label'], ['asc']),
    // 替選項補上 index，在父類、子類選單要插入新的下一層時，有 index 可以減少搜尋次數
    // 在主要頁面的 triggerCategoryOptionLoadingState 使用
    sortedOptions => _.map(sortedOptions, (option, index) => ({ ...option, index }))
  )()
  return convertedOptions
}

export const getLayerOptionsFromMenuData = (listing, menuData) => {
  const {
    subStationId,
    catId,
    catItemId
  } = listing
  // 根據賣場資料取得對應的規格表[父類、子類、規格]選項
  const layerOneOptions = _.get(menuData, `${CATEGORY_LAYER.ONE}.${subStationId}[0].children[0].children`, [])
  const layerTwoOptions = _.get(menuData, `${CATEGORY_LAYER.TWO}.${catId}[0].children`, [])
  const layerThreeOptions = _.get(menuData, `${CATEGORY_LAYER.THREE}.${catItemId}`, [])
  return {
    [CATEGORY_LAYER.ONE]: getVisibleOptionsWithIndex(layerOneOptions),
    [CATEGORY_LAYER.TWO]: getVisibleOptionsWithIndex(layerTwoOptions),
    [CATEGORY_LAYER.THREE]: getVisibleOptionsWithIndex(layerThreeOptions)
  }
}

export const getCategoryOptions = (listing, menuData) => {
  const {
    catId,
    catItemId
  } = listing
  const {
    [CATEGORY_LAYER.ONE]: layerOneOptions,
    [CATEGORY_LAYER.TWO]: layerTwoOptions,
    [CATEGORY_LAYER.THREE]: layerThreeOptions
  } = getLayerOptionsFromMenuData(listing, menuData)
  // 從已選擇的[父類、子類、規格]項目找出對應規格表選單的三層項目
  const layerOneIndex = _.findIndex(layerOneOptions, { value: catId })
  const layerTwoIndex = _.findIndex(layerTwoOptions, { value: catItemId })
  const categoryOptions = _.flow(
    // 從既有選單指定選項插入下一層，深拷貝避免產生副作用影響輸入的參數
    _.cloneDeep,
    // 從已選取的父類項目找出要插入子類項目在哪個父類 index
    oneLayerOptions => {
      _.set(oneLayerOptions, `${layerOneIndex}.children`, layerTwoOptions)
      return oneLayerOptions
    },
    // 從已選取的子類項目找出要插入規格項目在哪個子類 index
    twoLayerOptions => {
      _.set(twoLayerOptions, `${layerOneIndex}.children[${layerTwoIndex}].children`, layerThreeOptions)
      return twoLayerOptions
    }
  )(layerOneOptions)
  return categoryOptions
}

export const getCombindCategoryOptions = (listing, categoryOptions = [], currentMenuData) => {
  const {
    catId,
    catItemId
  } = listing
  // 從賣場資料取得目前選擇的規格表選單該顯示的項目
  // 父類、子類、規格選單"在 UI 上"有相依性(實際上互相是多對多關係，但有 UI 相依性選擇時比較容易)，
  // 所以需要逐層找出對應的選單項目
  // 先用賣場資料找出要被關聯的子類、規格選單項目
  const {
    [CATEGORY_LAYER.TWO]: layerTwoOptions,
    [CATEGORY_LAYER.THREE]: layerThreeOptions
  } = getLayerOptionsFromMenuData(listing, currentMenuData)
  const combindCategoryOptions = _.cloneDeep(categoryOptions)
  // 查詢子類要塞到哪一個父類項目底下
  const layerOneIndex = _.findIndex(categoryOptions, { value: catId })
  const layerTwoPath = `${layerOneIndex}.children`
  const newLayerTwoOptions = _.flow(
    () => _.get(categoryOptions, layerTwoPath, []),
    // 將儲存在指定父類底下已存在的子類選單做成對應表
    existLayerTwoOptions => _.keyBy(existLayerTwoOptions, 'value'),
    // 已存在的子類選單會有查過規格的紀錄(children)，保留下來下次查詢同樣規格時就不需要重查
    // 剩下沒查過的就只放該層預設的項目
    existLayerTwoMapByCatId => _.map(layerTwoOptions, option => {
      return _.get(existLayerTwoMapByCatId, option.value, option)
    })
  )()
  _.set(combindCategoryOptions, layerTwoPath, newLayerTwoOptions)

  if (!_.isUndefined(catItemId)) {
    const layerTwoIndex = _.findIndex(newLayerTwoOptions, { value: catItemId })
    const layerThreePath = `${layerTwoPath}[${layerTwoIndex}].children`
    const newLayerThreeOptions = _.flow(
      () => _.get(categoryOptions, layerThreePath, []),
      // 將儲存在指定子類底下已存在的規格選單做成對應表
      existLayerThreeMap => _.keyBy(existLayerThreeMap, 'value'),
      // 已存在的規格選單會有查過規格項目的紀錄(children)，保留下來下次查詢同樣規格項目時就不需要重查
      // 剩下沒查過的就只放該層預設的項目
      existLayerThreeMapByCatItemId => _.map(layerThreeOptions, option => {
        return _.get(existLayerThreeMapByCatItemId, option.value, option)
      })
    )()
    _.set(combindCategoryOptions, layerThreePath, newLayerThreeOptions)
  }

  return combindCategoryOptions
}

export const getCheckedAttributes = (listing) => {
  const { attributes } = listing
  // 取出有勾選規格項目，若無勾選回傳空陣列
  const findCheckedAttributes = _.filter(attributes, item => {
    return item[SELECTED_VALUES_SELECTED]
  })
  return findCheckedAttributes
}

export const getIsSmaeAttributesApply = (attrType, currentType) => {
  const isApply = !(attrType === ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX) && !(attrType === ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX)
  return isApply
}

export const getConvertedSelectedValues = (attrType, currentType, checkItem) => {
  const selectedValues = (attrType === ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX) || (attrType === ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX) || (attrType === ATTRIBUTE_CONSTRAINTS_TYPE.TEXT && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX) || (attrType === ATTRIBUTE_CONSTRAINTS_TYPE.TEXT && currentType === ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX) ? _.toString(checkItem.selectedValues) : checkItem.selectedValues
  return selectedValues
}

export const getCombindCheckedAttributes = (listing, item) => {
  // 將勾選的規格項目，套用所有賣場
  const map = _.reduce(getCheckedAttributes(listing), (newMap, checkItem) => {
    // 規格項目名稱包含才套用
    if (checkItem.name in newMap) {
      const attrOptions = _.get(newMap, `${checkItem.name}.options`, [])
      const attrType = _.get(newMap, `${checkItem.name}.constraints.type`, '')

      // constraints type "checkbox" or "radiobox"
      const isAttrTypeBox = [ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX, ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX].includes(attrType)
      const isSelectedValuesNotEmpty = !_.isEmpty(checkItem.selectedValues)
      const attrOptionsMapByValue = _.keyBy(attrOptions, 'value')
      const isNotAttrOptions = !_.some(checkItem.selectedValues, selectedValue => (selectedValue in attrOptionsMapByValue))
      const attrOptionsNotSelectedValues = isAttrTypeBox && isSelectedValuesNotEmpty && isNotAttrOptions

      // 判斷是checkbox or radiobox 、規格值不是空 、所勾選的規格值，不在規格選項裡不套用，其餘否則套用
      if (!attrOptionsNotSelectedValues) {
        const currentType = _.get(checkItem, 'constraints.type', '')
        // 例外狀況1：
        // 套用規格當前為多選值(type = checkBox)，套用至相同規格(type = radioBox)時，則不套用
        // 套用規格當前為單選值(type = radioBox)，套用至相同規格(type = checkBox)時，則不套用
        const isApply = getIsSmaeAttributesApply(attrType, currentType)
        if (isApply) {
          // 例外狀況2：
          // 套用規格當前為多選或單選值(type = checkBox或readioBox)，套用至相同規格名稱(type = custom或text)時，則selectedValues要轉字串，否則API會擋下來
          const selectedValues = getConvertedSelectedValues(attrType, currentType, checkItem)
          _.set(newMap[checkItem.name], 'selectedValues', selectedValues)
        }
      }
    }
    return newMap
  }, _.keyBy(item.attributes, 'name'))
  return map
}

export const getCheckedAttrMode = (listing) => {
  // 判斷是否有勾選商品規格
  const getListingAttrModeSelected = _.get(listing, ATTRIBUTE_DISPLAY_MODE_SELECTED, false)
  return getListingAttrModeSelected
}

export const getBatchApplyNewListings = (listing, listings) => {
  const newListings = _.map(listings, item => {
    // 所有賣場 attributes 更換成點擊的該賣場 attributes
    const newAttributes = getCombindCheckedAttributes(listing, item)
    item.attributes = _.values(newAttributes)

    // 如果有勾選商品規格才套用
    if (getCheckedAttrMode(listing)) {
      const { attributeDisplayMode } = listing
      item.attributeDisplayMode = attributeDisplayMode
    }
    return item
  })
  return newListings
}

export const getMappedErrorBatchResult = (batchResult) => {
  const { failedProposalMap } = batchResult
  if (_.isEmpty(failedProposalMap)) {
    return batchResult
  }

  const mappedFailedMap = _.mapValues(failedProposalMap, (failedInfo) => {
    const errorCode = _.get(failedInfo, 'error.code')
    if (errorCode) {
      const apiErrorMessage = _.get(API_ERROR_MAPPING, errorCode.toString())
      if (apiErrorMessage) {
        const newFailedInfo = _.cloneDeep(failedInfo)
        _.set(newFailedInfo, 'error.debugError.detail[0].message', apiErrorMessage)
        return newFailedInfo
      }
    }
    return failedInfo
  })
  return { ...batchResult, failedProposalMap: mappedFailedMap }
}
